import { makeStyles } from '@material-ui/core';
import { FC, useEffect, useState } from 'react';
import CButton from 'src/component/Button';
import {
  AndroidApp,
  AppleApp
} from 'src/services/napi/projects/interfaces/fetchProjects.interface';
import { AppType } from 'src/page/DynamicLinksConfig/interfaces/appType.interface';
import AddOrEditAppModal from 'src/page/DynamicLinksConfig/components/AddOrEditAppModal';
import ProjectAppsSidebarItem from 'src/page/DynamicLinksConfig/components/ProjectAppsSidebarItem';
import ProjectAppsAppDetails from 'src/page/DynamicLinksConfig/components/ProjectAppsAppDetails';

const useStyle = makeStyles({
  card: {
    border: '1px solid #CAD8FF',
    borderRadius: '8px',
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateColumns: '260px auto',
    overflow: 'hidden'
  },
  header: {
    alignItems: 'center',
    background: '#F0F4FF55',
    borderBottom: '1px solid #CAD8FF',
    display: 'flex',
    gridColumn: 'span 2',
    height: 'min-content',
    justifyContent: 'space-between',
    padding: '8px 8px 8px 16px'
  },
  headerTitle: {
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: '22px'
  },
  sidebar: {
    background: 'linear-gradient(184.7deg, #F0F4FF55, #ffffff)',
    borderRight: '1px solid #CAD8FF',
    minHeight: '100px',
    textAlign: 'left'
  },
  sidebarSectionTitle: {
    color: 'rgba(0, 0, 0, 0.55)',
    fontSize: '13px',
    lineHeight: '20px',
    padding: '16px 20px 4px'
  }
});

interface ProjectAppsProps {
  androidApps: AndroidApp[];
  appleApps: AppleApp[];
  handleUpdateApps: (apps: { androidApps: AndroidApp[]; appleApps: AppleApp[] }) => void;
}

const ProjectApps: FC<ProjectAppsProps> = ({ androidApps, appleApps, handleUpdateApps }) => {
  const classes = useStyle();

  const [selectedApp, setSelectedApp] = useState({
    id: androidApps[0]?.packageName ?? appleApps[0]?.bundleId,
    type: androidApps.length || !appleApps.length ? AppType.android : AppType.apple
  });

  const selectedAppData =
    selectedApp.type === AppType.android
      ? androidApps.find((app) => app.packageName === selectedApp.id)
      : appleApps.find((app) => app.bundleId === selectedApp.id);

  const [isAddAppModalOpen, setIsAddAppModalOpen] = useState(false);

  const [isEditAppModalOpen, setIsEditAppModalOpen] = useState(false);

  useEffect(() => {
    if (!androidApps || !appleApps) {
      return;
    }

    setSelectedApp({
      id: androidApps[0]?.packageName ?? appleApps[0]?.bundleId,
      type: androidApps.length || !appleApps.length ? AppType.android : AppType.apple
    });
  }, [androidApps, appleApps]);

  return (
    <>
      <div className={classes.card}>
        <div className={classes.header}>
          <div className={classes.headerTitle}>Apps</div>

          <CButton onClick={() => setIsAddAppModalOpen(true)} type="primary">
            Add app
          </CButton>
        </div>

        <div className={classes.sidebar}>
          {!!androidApps.length && <div className={classes.sidebarSectionTitle}>Android apps</div>}

          {androidApps.map((app) => (
            <ProjectAppsSidebarItem
              key={`name=${app.name}, packageName=${app.packageName}`}
              appData={app}
              appType={AppType.android}
              onClick={() => setSelectedApp({ id: app.packageName, type: AppType.android })}
              selected={selectedApp.type === AppType.android && selectedApp.id === app.packageName}
            />
          ))}

          {!!appleApps.length && <div className={classes.sidebarSectionTitle}>Apple apps</div>}

          {appleApps.map((app) => (
            <ProjectAppsSidebarItem
              key={`name=${app.name}, bundleId=${app.bundleId}`}
              appData={app}
              appType={AppType.apple}
              onClick={() => setSelectedApp({ id: app.bundleId, type: AppType.apple })}
              selected={selectedApp.type === AppType.apple && selectedApp.id === app.bundleId}
            />
          ))}
        </div>

        {selectedAppData && (
          <ProjectAppsAppDetails
            appData={selectedAppData}
            appType={selectedApp.type}
            onClickEdit={() => setIsEditAppModalOpen(true)}
          />
        )}
      </div>

      <AddOrEditAppModal
        androidApps={androidApps}
        appleApps={appleApps}
        handleClose={() => {
          setIsAddAppModalOpen(false);
        }}
        handleUpdateApps={(updatedApps: { androidApps: AndroidApp[]; appleApps: AppleApp[] }) => {
          handleUpdateApps(updatedApps);
          setIsAddAppModalOpen(false);
        }}
        isOpen={isAddAppModalOpen}
      />

      {selectedAppData && (
        <AddOrEditAppModal
          androidApps={androidApps}
          appleApps={appleApps}
          handleClose={() => {
            setIsEditAppModalOpen(false);
          }}
          handleUpdateApps={(updatedApps: { androidApps: AndroidApp[]; appleApps: AppleApp[] }) => {
            handleUpdateApps(updatedApps);
            setIsEditAppModalOpen(false);
          }}
          initialDraft={{ data: { ...selectedAppData }, type: selectedApp.type }}
          isOpen={isEditAppModalOpen}
        />
      )}
    </>
  );
};

export default ProjectApps;
